/**
 * Resolves a displayable inventory count from heterogeneous product `data` shapes
 * (camelCase keys, stock_quantity, nested { count }, sync provider snapshots).
 */

const EXACT_KEYS = [
  'stock',
  'stock_quantity',
  'stockqty',
  'stock_count',
  'stockcount',
  'stock_level',
  'stocklevel',
  'inventory',
  'inventory_count',
  'inventory_quantity',
  'inventoryquantity',
  'inventory_qty',
  'quantity',
  'qty',
  'available',
  'available_stock',
  'available_quantity',
  'availablequantity',
  'quantity_available',
  'quantityavailable',
  'on_hand',
  'onhand',
  'in_stock',
  'instock',
  'current_stock',
  'currentstock',
  'units',
  'unit_count',
  'num_in_stock',
  'numinstock',
  'qty_on_hand',
  'quantity_on_hand',
] as const;

/** Skip flat keys that look like pricing, orders, or non-level fields */
const SKIP_KEY_PATTERN =
  /min_order|minimum_order|reorder|reserved|price|cost|image|compare_at|fulfillment|weight/i;

/**
 * Skip loose-matched keys that contain stock/inventory words but are not quantity fields.
 */
const LOOSE_KEY_BAD =
  /sku|stockkeeping|stock_keeping|inventory_policy|inventory_management|track_inventory|restock|backorder|stock_photo|stock_image|inventory_item_id|inventory_adjustment/i;

const NESTED_SCALAR_KEYS = [
  'count',
  'qty',
  'quantity',
  'value',
  'available',
  'current',
  'stock',
  'total',
  'inventory_quantity',
  'available_quantity',
] as const;

function parseFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim().replace(/,/g, '');
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function coerceInventoryScalar(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;

  const direct = parseFiniteNumber(value);
  if (direct !== null) return String(direct);

  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const o = value as Record<string, unknown>;
    for (const k of NESTED_SCALAR_KEYS) {
      if (o[k] !== undefined && o[k] !== null) {
        const inner = coerceInventoryScalar(o[k]);
        if (inner != null) return inner;
      }
    }
  }
  return null;
}

/** Map each raw key to normalized variants (handles camelCase → snake for matching). */
function putKeyVariants(map: Record<string, unknown>, rawKey: string, value: unknown) {
  const k = String(rawKey).trim();
  if (!k) return;
  const simple = k.toLowerCase().replace(/\s+/g, '_');
  map[simple] = value;
  const camelSnake = k
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (camelSnake !== simple) {
    map[camelSnake] = value;
  }
}

function buildNormalizedMap(dataObj: Record<string, unknown>): Record<string, unknown> {
  const lower: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(dataObj)) {
    putKeyVariants(lower, k, v);
  }
  return lower;
}

function resolveInventoryFromNormalizedMap(lower: Record<string, unknown>): string | null {
  for (const key of EXACT_KEYS) {
    if (!(key in lower)) continue;
    const s = coerceInventoryScalar(lower[key]);
    if (s != null) return s;
  }

  for (const [k, v] of Object.entries(lower)) {
    if (SKIP_KEY_PATTERN.test(k)) continue;
    if (
      /\b(qty|stock|inventory|quantity|on_?hand|available)\b/.test(k) ||
      k.endsWith('_stock') ||
      k.endsWith('_qty') ||
      k.endsWith('_quantity')
    ) {
      if (LOOSE_KEY_BAD.test(k)) continue;
      const s = coerceInventoryScalar(v);
      if (s != null) return s;
    }
  }

  for (const [k, v] of Object.entries(lower)) {
    if (SKIP_KEY_PATTERN.test(k) || LOOSE_KEY_BAD.test(k)) continue;
    if (k.includes('inventory') || k.includes('stock') || /(^|_)qty($|_)/.test(k)) {
      const s = coerceInventoryScalar(v);
      if (s != null) return s;
    }
  }

  return null;
}

/**
 * Returns a string suitable for UI (badge count) or null if no inventory field is found.
 */
export function getProductInventoryScalar(
  dataObj: Record<string, unknown> | undefined | null
): string | null {
  if (!dataObj || typeof dataObj !== 'object') return null;
  const lower = buildNormalizedMap(dataObj);
  return resolveInventoryFromNormalizedMap(lower);
}

export type InventorySourceItem = {
  data?: Record<string, unknown> | null;
  syncMetadata?: {
    externalData?: Record<string, unknown> | null;
    [key: string]: unknown;
  } | null;
};

/**
 * Use for list cards (Data browser, store): merges `data` and synced `externalData` snapshots.
 */
export function getDataItemInventoryScalar(item: InventorySourceItem | null | undefined): string | null {
  if (!item) return null;
  const fromData = getProductInventoryScalar(item.data ?? null);
  if (fromData != null) return fromData;
  const ext = item.syncMetadata?.externalData;
  if (ext && typeof ext === 'object' && !Array.isArray(ext)) {
    return getProductInventoryScalar(ext as Record<string, unknown>);
  }
  return null;
}

/**
 * Parsed units in stock for caps and validation, or null when inventory is unknown / non-numeric.
 */
export function getDataItemInventoryNumber(item: InventorySourceItem | null | undefined): number | null {
  const s = getDataItemInventoryScalar(item);
  if (s == null) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return n;
}
